/** Professional account lifecycle — REChecklist only. Buyer/seller checklist state is never stored here. */

import { getById, getUserByEmail, listTable, updateRow, writeAudit } from "../db";
import { checkCredential } from "../db/credentials";

import {
  ADMIN_SUSPENSION_DAYS,
  USER_SUSPENSION_DAYS,
  freezeBillingForUser,
  resumeBillingForUser,
  suspensionLimitDays,
} from "../billing/lifecycleBilling.js";

const KEY = "dh_account_lifecycle";
const NOTICE_KEY = "dh_lifecycle_notices";
const GRACE_DAYS = 30;

export { USER_SUSPENSION_DAYS, ADMIN_SUSPENSION_DAYS };

export const ACCOUNT_STATUS = {
  active: "active",
  suspended: "suspended",
  cancelled: "cancelled",
  compliance_lock: "compliance_lock",
  pending_reactivation: "pending_reactivation",
};

export const RETENTION_POLICY = {
  transactionYears: 3,
  graceDays: GRACE_DAYS,
  userSuspensionDays: USER_SUSPENSION_DAYS,
  adminSuspensionDays: ADMIN_SUSPENSION_DAYS,
  cancelNoticeHours: 72,
  summary:
    "Transaction records and client communications are kept for three years so the office can meet customary real estate retention practice. Agent personal profile data is removed after that period. The brokerage keeps archive access. Client checklist and portfolio work is stored locally on the device — export JSON/CSV/PDF from /backup on Windows PC or Apple Mac before suspension, cancellation, or closure. Restore points are device-local and capped; they are not a substitute for export. This is a workflow aid, not a California DRE certification.",
};

export const STATUS_COPY = {
  active: {
    label: "Active",
    login: "",
    desk: "Your account is in good standing.",
  },
  suspended: {
    label: "Suspended",
    login: "Account Suspended. Sign-in is paused. Your files remain with the office.",
    desk: "This account is suspended. Desk tools are paused. Client portals stay available only if the broker allows it.",
  },
  cancelled: {
    label: "Cancelled",
    login: "Account Cancelled. Sign-in is closed. The office retains required transaction records.",
    desk: "This account is cancelled. A grace period may still allow reactivation.",
  },
  compliance_lock: {
    label: "Compliance Lock",
    login: "Account Locked for Compliance Review. Please contact your broker.",
    desk: "This account is locked for a quiet compliance review. Only the broker can open it.",
  },
  pending_reactivation: {
    label: "Pending Reactivation",
    login: "Reactivation is waiting for broker approval.",
    desk: "A reactivation request is with the broker.",
  },
};

function readAll() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "{}");
  } catch {
    return {};
  }
}

function writeAll(map) {
  localStorage.setItem(KEY, JSON.stringify(map));
}

function emptyRecord(userId) {
  return {
    userId,
    status: ACCOUNT_STATUS.active,
    allowClientPortals: true,
    graceUntil: null,
    cancelledAt: null,
    lockedAt: null,
    suspendedAt: null,
    suspensionUntil: null,
    suspensionKind: null,
    reason: "",
    history: [],
    updated_at: null,
  };
}

function addDays(iso, days) {
  const date = new Date(iso || Date.now());
  date.setDate(date.getDate() + Number(days || 0));
  return date.toISOString();
}

export function getLifecycle(userId) {
  if (!userId) return emptyRecord("");
  const stored = readAll()[userId];
  const user = getById("users", userId);
  const fromUser = user?.account_status;
  const base = { ...emptyRecord(userId), ...(stored || {}) };
  if (fromUser && !stored?.status) base.status = fromUser;
  return base;
}

export function statusLabel(status) {
  return STATUS_COPY[status]?.label || "Active";
}

export function loginMessageForUser(user) {
  if (!user) return "";
  const life = getLifecycle(user.id);
  if (life.status === ACCOUNT_STATUS.active) return "";
  if (life.status === ACCOUNT_STATUS.cancelled && isGraceOpen(life)) {
    return `${STATUS_COPY.cancelled.login} A grace period is still open if your broker approves a return.`;
  }
  return STATUS_COPY[life.status]?.login || "";
}

export function isGraceOpen(life) {
  if (!life?.graceUntil) return false;
  return Date.parse(life.graceUntil) > Date.now();
}

export function assertProfessionalMayLogin(user) {
  const life = getLifecycle(user?.id);
  if (!life || life.status === ACCOUNT_STATUS.active) return;
  const error = new Error(loginMessageForUser(user));
  error.code = life.status;
  error.userId = user.id;
  error.graceOpen = isGraceOpen(life);
  throw error;
}

export function isLifecycleLoginError(error) {
  return Boolean(error?.code && ACCOUNT_STATUS[error.code] && error.code !== ACCOUNT_STATUS.active);
}

export function reactivateFromCredentials({ email, password }) {
  const user = getUserByEmail(email);
  let agent = null;
  try {
    agent = JSON.parse(localStorage.getItem("dh_agents") || "[]").find(
      (item) => String(item.email || "").toLowerCase() === String(email || "").toLowerCase()
    );
  } catch {
    agent = null;
  }
  const credOk = Boolean(
    (user && checkCredential(user.id, password)) || (agent && agent.password === password)
  );
  if (!credOk || (!user && !agent)) {
    throw new Error("Those credentials did not match.");
  }
  const subject = user || { id: agent.id, role_type: "agent" };
  requestReactivate(subject.id, { userId: subject.id, role: subject.role_type || "agent" });
  const life = getLifecycle(subject.id);
  if (life.status !== ACCOUNT_STATUS.active) {
    const error = new Error(STATUS_COPY.pending_reactivation.login);
    error.code = life.status;
    error.userId = subject.id;
    throw error;
  }
  return subject;
}

export function maybeWarnGracePeriod(userId) {
  const life = getLifecycle(userId);
  if (life.status !== ACCOUNT_STATUS.cancelled || !life.graceUntil) return;
  const left = Date.parse(life.graceUntil) - Date.now();
  const seven = 7 * 24 * 60 * 60 * 1000;
  if (left > 0 && left <= seven) {
    const notices = readNotices();
    if (notices.some((row) => row.userId === userId && row.action === "grace_warning")) return;
    queueNotices(userId, "grace_warning", { userId, role: "system" });
  }
}

function logEvent(userId, action, actor, metadata = {}) {
  const all = readAll();
  const current = { ...emptyRecord(userId), ...(all[userId] || {}) };
  const row = {
    at: new Date().toISOString(),
    action,
    actorId: actor?.userId || null,
    actorRole: actor?.role || null,
    metadata,
  };
  current.history = [row, ...(current.history || [])].slice(0, 40);
  current.updated_at = row.at;
  all[userId] = current;
  writeAll(all);
  try {
    writeAudit({
      user_id: actor?.userId || userId,
      action_type: `account.${action}`,
      transaction_id: null,
      metadata: { subject_id: userId, ...metadata },
    });
  } catch {
    /* ignore */
  }
  return current;
}

function persist(userId, patch, actor, action) {
  const all = readAll();
  const next = { ...emptyRecord(userId), ...(all[userId] || {}), ...patch, updated_at: new Date().toISOString() };
  all[userId] = next;
  writeAll(all);
  try {
    updateRow("users", userId, {
      account_status: next.status,
      status: next.status === ACCOUNT_STATUS.active ? "active" : next.status,
    });
  } catch {
    /* ignore */
  }
  logEvent(userId, action, actor, { status: next.status, reason: next.reason });
  queueNotices(userId, action, actor);
  return next;
}

function queueNotices(userId, action, actor) {
  const subject = getById("users", userId);
  const brokerId = subject?.brokerage_id || (subject?.role_type === "broker" ? subject.id : null);
  const lines = {
    suspend: {
      agent: "Your REChecklist account is suspended. Desk sign-in is paused. Files stay with the office.",
      broker: `${subject?.name || "An agent"} suspended their account, or it was suspended for the office.`,
    },
    cancel: {
      agent: "Your REChecklist account is cancelled. A grace period may still allow a return with broker approval.",
      broker: `${subject?.name || "An agent"} cancelled their account.`,
    },
    reactivate: {
      agent: "Your REChecklist account is active again. Desk and client portals may resume.",
      broker: `${subject?.name || "An agent"} account was reactivated.`,
    },
    lock: {
      agent: "Your account is locked for a compliance review. Please contact your broker.",
      broker: `A compliance lock is on ${subject?.name || "an agent"}.`,
    },
    unlock: {
      agent: "The compliance lock has been released.",
      broker: `The compliance lock on ${subject?.name || "an agent"} was released.`,
    },
    grace_warning: {
      agent: "The cancellation grace period is ending soon. Speak with your broker if you wish to return.",
      broker: `Grace period is ending for ${subject?.name || "an agent"}.`,
    },
  };
  const pack = lines[action];
  if (!pack) return;
  const notices = readNotices();
  notices.unshift({
    id: crypto.randomUUID(),
    at: new Date().toISOString(),
    userId,
    action,
    agentText: pack.agent,
    brokerText: pack.broker,
    actorId: actor?.userId || null,
    brokerId,
    sent: { agent: true, broker: Boolean(brokerId) },
  });
  localStorage.setItem(NOTICE_KEY, JSON.stringify(notices.slice(0, 80)));
}

export function readNotices() {
  try {
    return JSON.parse(localStorage.getItem(NOTICE_KEY) || "[]");
  } catch {
    return [];
  }
}

export function noticesForSession(session) {
  const all = readNotices();
  if (session?.role === "broker" || session?.role === "admin") {
    return all.filter((row) => row.brokerId === session.userId || session.role === "admin");
  }
  return all.filter((row) => row.userId === session?.userId);
}

function suspensionKindForActor(actor) {
  if (actor?.role === "admin") return "admin";
  if (actor?.role === "broker") return "broker";
  return "user";
}

export function suspendAccount(userId, actor, { reason = "", allowClientPortals = true } = {}) {
  const days = suspensionLimitDays(actor);
  const now = new Date().toISOString();
  freezeBillingForUser(userId, { reason });
  return persist(
    userId,
    {
      status: ACCOUNT_STATUS.suspended,
      reason,
      allowClientPortals,
      suspendedAt: now,
      suspensionUntil: addDays(now, days),
      suspensionKind: suspensionKindForActor(actor),
    },
    actor,
    "suspend"
  );
}

export function adminSuspendAccount(userId, actor, { reason = "", allowClientPortals = false } = {}) {
  if (actor?.role !== "admin") throw new Error("Admin desk only.");
  return suspendAccount(userId, { ...actor, role: "admin" }, { reason, allowClientPortals });
}

export function cancelAccount(userId, actor, { reason = "" } = {}) {
  freezeBillingForUser(userId, { reason: reason || "Account cancelled" });
  const graceUntil = new Date(Date.now() + GRACE_DAYS * 24 * 60 * 60 * 1000).toISOString();
  return persist(
    userId,
    {
      status: ACCOUNT_STATUS.cancelled,
      reason,
      cancelledAt: new Date().toISOString(),
      graceUntil,
      allowClientPortals: false,
    },
    actor,
    "cancel"
  );
}

function clearSuspensionFields() {
  return {
    suspendedAt: null,
    suspensionUntil: null,
    suspensionKind: null,
  };
}

export function requestReactivate(userId, actor, { viaPayment = false } = {}) {
  const life = getLifecycle(userId);
  if (life.status === ACCOUNT_STATUS.suspended) {
    if (!viaPayment) resumeBillingForUser(userId, { fromPayment: false });
    return persist(
      userId,
      {
        status: ACCOUNT_STATUS.active,
        reason: "",
        allowClientPortals: true,
        ...clearSuspensionFields(),
      },
      actor,
      "reactivate"
    );
  }
  if (life.status === ACCOUNT_STATUS.cancelled && isGraceOpen(life)) {
    if (actor?.role === "broker" || actor?.role === "admin" || viaPayment) {
      resumeBillingForUser(userId, { fromPayment: viaPayment });
      return persist(
        userId,
        {
          status: ACCOUNT_STATUS.active,
          reason: "",
          graceUntil: null,
          allowClientPortals: true,
          ...clearSuspensionFields(),
        },
        actor,
        "reactivate"
      );
    }
    return persist(userId, { status: ACCOUNT_STATUS.pending_reactivation }, actor, "reactivate");
  }
  if (life.status === ACCOUNT_STATUS.pending_reactivation && (actor?.role === "broker" || actor?.role === "admin")) {
    resumeBillingForUser(userId, { fromPayment: false });
    return persist(
      userId,
      {
        status: ACCOUNT_STATUS.active,
        reason: "",
        graceUntil: null,
        allowClientPortals: true,
        ...clearSuspensionFields(),
      },
      actor,
      "reactivate"
    );
  }
  throw new Error("This account cannot be reactivated from its current state.");
}

export function adminReactivateAccount(userId, actor, { note = "" } = {}) {
  if (actor?.role !== "admin") throw new Error("Admin desk only.");
  resumeBillingForUser(userId, { fromPayment: false });
  return persist(
    userId,
    {
      status: ACCOUNT_STATUS.active,
      reason: String(note || "").trim(),
      graceUntil: null,
      allowClientPortals: true,
      lockedAt: null,
      ...clearSuspensionFields(),
    },
    actor,
    "reactivate"
  );
}

export function lockForCompliance(userId, actor, { reason = "" } = {}) {
  freezeBillingForUser(userId, { reason: reason || "Compliance lock" });
  return persist(
    userId,
    {
      status: ACCOUNT_STATUS.compliance_lock,
      reason,
      lockedAt: new Date().toISOString(),
      allowClientPortals: false,
    },
    actor,
    "lock"
  );
}

export function unlockCompliance(userId, actor) {
  resumeBillingForUser(userId, { fromPayment: false });
  return persist(
    userId,
    {
      status: ACCOUNT_STATUS.active,
      reason: "",
      lockedAt: null,
      allowClientPortals: true,
      ...clearSuspensionFields(),
    },
    actor,
    "unlock"
  );
}

/** Expire long-running suspensions — user 90d, office 180d. */
export function tickSuspensionTimers() {
  const all = readAll();
  const now = Date.now();
  let moved = 0;
  Object.entries(all).forEach(([userId, life]) => {
    if (life.status !== ACCOUNT_STATUS.suspended || !life.suspensionUntil) return;
    if (Date.parse(life.suspensionUntil) > now) return;
    cancelAccount(userId, { userId, role: "system" }, {
      reason: `Suspension period ended (${life.suspensionKind || "user"} · ${life.suspensionKind === "admin" || life.suspensionKind === "broker" ? ADMIN_SUSPENSION_DAYS : USER_SUSPENSION_DAYS} days).`,
    });
    moved += 1;
  });
  return { moved };
}

export function professionalMayUseDesk(userId) {
  const status = getLifecycle(userId).status;
  return status === ACCOUNT_STATUS.active;
}

export function clientPortalAllowed(client) {
  if (!client) return false;
  if (client.status && client.status !== "active") return false;
  const open = listTable("transactions").find(
    (row) => row.client_id === client.id && row.status === "open" && row.agent_id
  );
  const agentId = open?.agent_id;
  if (!agentId) return true;
  const life = getLifecycle(agentId);
  if (life.status === ACCOUNT_STATUS.compliance_lock || life.status === ACCOUNT_STATUS.cancelled) return false;
  if (life.status === ACCOUNT_STATUS.suspended) return Boolean(life.allowClientPortals);
  return true;
}

export function listBrokerAgents(brokerId) {
  return listTable("users").filter((user) => user.role_type === "agent" && user.brokerage_id === brokerId);
}

export function retentionPolicyText() {
  return RETENTION_POLICY;
}
